import { TicketModel, renderTicketEscPos, DEFAULT_CODE_PAGE } from '../utils/escpos';
import { readPaperWidth } from '../utils/printerConfig';

/**
 * Impresora termica de tickets enchufada directamente al navegador (WebUSB o
 * puerto serie), sin pasar por el sistema de impresion del sistema operativo.
 *
 * Se elige UNA vez en Ajustes (el navegador pide permiso con su selector) y
 * queda guardada: desde entonces cada ticket sale sin ventana y sin depender de
 * la impresora predeterminada. La factura NO pasa por aqui (es un A4): sigue
 * imprimiendose con el navegador.
 *
 * Solo Chrome y Edge (WebUSB / Web Serial) y solo impresoras ESC/POS. En
 * Windows, una impresora USB necesita el controlador WinUSB para que el
 * navegador pueda abrirla (el controlador normal de impresion la reserva).
 */
export type ThermalMode = 'browser' | 'usb' | 'serial';

export interface ThermalConfig {
    mode: ThermalMode;
    /** Nombre con el que el navegador identifica el dispositivo, para enseñarlo en Ajustes. */
    name: string;
    vendorId?: number;
    productId?: number;
    /** Solo puerto serie. */
    baudRate: number;
    /** Sin acentos ni €: si la impresora no tiene la pagina de codigos CP858. */
    ascii: boolean;
}

export const THERMAL_PRINTER_KEY = 'tpv_thermal_printer';
export const BAUD_RATES = [9600, 19200, 38400, 57600, 115200];

export const DEFAULT_THERMAL: ThermalConfig = { mode: 'browser', name: '', baudRate: 9600, ascii: false };

export const getThermalConfig = (): ThermalConfig => {
    try {
        const raw = localStorage.getItem(THERMAL_PRINTER_KEY);
        if (!raw) return DEFAULT_THERMAL;
        const saved = JSON.parse(raw);
        const mode: ThermalMode = saved.mode === 'usb' || saved.mode === 'serial' ? saved.mode : 'browser';
        return {
            mode,
            name: typeof saved.name === 'string' ? saved.name : '',
            vendorId: typeof saved.vendorId === 'number' ? saved.vendorId : undefined,
            productId: typeof saved.productId === 'number' ? saved.productId : undefined,
            baudRate: BAUD_RATES.indexOf(Number(saved.baudRate)) !== -1 ? Number(saved.baudRate) : DEFAULT_THERMAL.baudRate,
            ascii: !!saved.ascii
        };
    } catch {
        return DEFAULT_THERMAL;
    }
};

export const saveThermalConfig = (config: ThermalConfig): void => {
    try {
        localStorage.setItem(THERMAL_PRINTER_KEY, JSON.stringify(config));
    } catch { /* sin persistencia: vuelve al navegador al recargar */ }
};

const usbApi = (): any => (typeof navigator !== 'undefined' ? (navigator as any).usb : undefined);
const serialApi = (): any => (typeof navigator !== 'undefined' ? (navigator as any).serial : undefined);

export const isUsbSupported = (): boolean => !!usbApi();
export const isSerialSupported = (): boolean => !!serialApi();

const hex = (n: number | undefined): string => (n === undefined ? '????' : n.toString(16).padStart(4, '0'));

const isUserCancel = (err: any): boolean => !!err && (err.name === 'NotFoundError' || err.name === 'AbortError');

// ── Elegir la impresora (una vez, en Ajustes) ─────────────────────────────

/** Abre el selector del navegador y devuelve el dispositivo USB elegido. Hay que llamarlo desde un clic. */
export const pickUsbPrinter = async (): Promise<Partial<ThermalConfig>> => {
    const usb = usbApi();
    if (!usb) throw new Error('Este navegador no permite impresoras USB. Usa Chrome o Edge.');
    try {
        const device = await usb.requestDevice({ filters: [] });
        return {
            mode: 'usb',
            vendorId: device.vendorId,
            productId: device.productId,
            name: device.productName || `USB ${hex(device.vendorId)}:${hex(device.productId)}`
        };
    } catch (err) {
        if (isUserCancel(err)) throw new Error('No se eligió ninguna impresora.');
        throw err;
    }
};

export const pickSerialPrinter = async (): Promise<Partial<ThermalConfig>> => {
    const serial = serialApi();
    if (!serial) throw new Error('Este navegador no permite puertos serie. Usa Chrome o Edge.');
    try {
        const port = await serial.requestPort();
        const info = (port.getInfo && port.getInfo()) || {};
        return {
            mode: 'serial',
            vendorId: info.usbVendorId,
            productId: info.usbProductId,
            name: info.usbVendorId !== undefined ? `Puerto serie ${hex(info.usbVendorId)}:${hex(info.usbProductId)}` : 'Puerto serie'
        };
    } catch (err) {
        if (isUserCancel(err)) throw new Error('No se eligió ninguna impresora.');
        throw err;
    }
};

// ── Escribir en la impresora ──────────────────────────────────────────────

/** Muchas termicas tienen un buffer pequeño: se manda por trozos. */
const CHUNK_BYTES = 4096;

/** El primer endpoint de salida (bulk) del dispositivo, prefiriendo la interfaz de clase impresora (7). */
export const findOutEndpoint = (device: any): { interfaceNumber: number; alternateSetting: number; endpointNumber: number } | null => {
    const found: { interfaceNumber: number; alternateSetting: number; endpointNumber: number; isPrinter: boolean }[] = [];
    const interfaces = (device.configuration && device.configuration.interfaces) || [];
    interfaces.forEach((iface: any) => {
        (iface.alternates || []).forEach((alt: any) => {
            (alt.endpoints || []).forEach((ep: any) => {
                if (ep.direction === 'out' && ep.type === 'bulk') {
                    found.push({
                        interfaceNumber: iface.interfaceNumber,
                        alternateSetting: alt.alternateSetting,
                        endpointNumber: ep.endpointNumber,
                        isPrinter: alt.interfaceClass === 7
                    });
                }
            });
        });
    });
    const best = found.filter(f => f.isPrinter)[0] || found[0];
    return best ? { interfaceNumber: best.interfaceNumber, alternateSetting: best.alternateSetting, endpointNumber: best.endpointNumber } : null;
};

const writeUsb = async (config: ThermalConfig, data: Uint8Array): Promise<void> => {
    const usb = usbApi();
    if (!usb) throw new Error('Este navegador no permite impresoras USB. Usa Chrome o Edge.');

    const devices: any[] = await usb.getDevices();
    const device = devices.filter(d => d.vendorId === config.vendorId && d.productId === config.productId)[0];
    if (!device) {
        throw new Error('No se encuentra la impresora USB. Comprueba que está conectada y encendida, o vuelve a elegirla en Ajustes.');
    }

    await device.open();
    try {
        if (device.configuration === null) await device.selectConfiguration(1);
        const target = findOutEndpoint(device);
        if (!target) throw new Error('La impresora USB no tiene un canal de escritura compatible.');

        await device.claimInterface(target.interfaceNumber);
        try {
            for (let offset = 0; offset < data.length; offset += CHUNK_BYTES) {
                await device.transferOut(target.endpointNumber, data.slice(offset, offset + CHUNK_BYTES));
            }
        } finally {
            try { await device.releaseInterface(target.interfaceNumber); } catch { /* ya liberada */ }
        }
    } finally {
        try { await device.close(); } catch { /* ya cerrada */ }
    }
};

const writeSerial = async (config: ThermalConfig, data: Uint8Array): Promise<void> => {
    const serial = serialApi();
    if (!serial) throw new Error('Este navegador no permite puertos serie. Usa Chrome o Edge.');

    const ports: any[] = await serial.getPorts();
    const port = ports.filter(p => {
        const info = (p.getInfo && p.getInfo()) || {};
        // Sin identificador (puerto Bluetooth, por ejemplo) no hay con que distinguirlos: vale el primero.
        return config.vendorId === undefined || (info.usbVendorId === config.vendorId && info.usbProductId === config.productId);
    })[0];
    if (!port) {
        throw new Error('No se encuentra la impresora serie. Comprueba que está conectada o vuelve a elegirla en Ajustes.');
    }

    await port.open({ baudRate: config.baudRate });
    try {
        const writer = port.writable.getWriter();
        try {
            await writer.write(data);
            await writer.close();     // espera a que se vacie lo escrito antes de cerrar el puerto
        } catch (err) {
            try { writer.releaseLock(); } catch { /* ya liberado */ }
            throw err;
        }
    } finally {
        try { await port.close(); } catch { /* ya cerrado */ }
    }
};

/** Manda bytes a la impresora configurada. */
export const printRaw = async (config: ThermalConfig, data: Uint8Array): Promise<void> => {
    if (config.mode === 'usb') return writeUsb(config, data);
    if (config.mode === 'serial') return writeSerial(config, data);
    throw new Error('No hay impresora térmica configurada.');
};

/** El ticket en ESC/POS, con las columnas del rollo elegido en Ajustes (48 para 80 mm, 32 para 58 mm). */
export const printTicketRaw = async (model: TicketModel, config: ThermalConfig = getThermalConfig()): Promise<void> => {
    const columns = readPaperWidth() === 58 ? 32 : 48;
    await printRaw(config, renderTicketEscPos(model, { columns, codePage: DEFAULT_CODE_PAGE, ascii: config.ascii }));
};
